import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Button, Modal, Field, Input, Empty } from "@/components/ui-kit";
import { Plus, Pencil, Trash2, MapPin } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", address: "", note: "" };

export default function Locations() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => { setLoading(true); api.get("/locations").then((r) => setLocations(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (l) => { setEditing(l); setForm({ ...empty, ...l }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/locations/${editing.id}`, form);
      else await api.post("/locations", form);
      toast.success(editing ? "Lieu mis à jour" : "Lieu ajouté");
      setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); } finally { setSaving(false); }
  };

  const remove = async (id) => { if (!window.confirm("Supprimer ce lieu ?")) return; await api.delete(`/locations/${id}`); toast.success("Lieu supprimé"); load(); };

  return (
    <div data-testid="locations-page">
      <PageHeader title="Lieux" subtitle="Gérez les lieux et adresses utilisés dans le planning."
        action={isAdmin && <Button data-testid="add-location-btn" onClick={openNew}><Plus size={16} /> Ajouter un lieu</Button>} />

      {loading ? <Empty text="Chargement..." /> : locations.length ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {locations.map((l) => (
            <Card key={l.id} data-testid="location-card" className="flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 shrink-0 rounded-lg bg-primary/15 flex items-center justify-center text-primary"><MapPin size={18} /></div>
                  <div className="min-w-0">
                    <p className="font-bold leading-tight truncate">{l.name}</p>
                    {l.address && <p className="text-xs text-muted-foreground mt-0.5">{l.address}</p>}
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex gap-1 shrink-0">
                    <button data-testid={`edit-location-btn-${l.id}`} onClick={() => openEdit(l)} className="p-1.5 text-muted-foreground hover:text-primary"><Pencil size={14} /></button>
                    <button data-testid={`delete-location-btn-${l.id}`} onClick={() => remove(l.id)} className="p-1.5 text-muted-foreground hover:text-destructive"><Trash2 size={14} /></button>
                  </div>
                )}
              </div>
              {l.note && <p className="text-sm text-muted-foreground mt-3">{l.note}</p>}
            </Card>
          ))}
        </div>
      ) : <Empty text={isAdmin ? "Aucun lieu. Cliquez sur « Ajouter un lieu »." : "Aucun lieu enregistré."} />}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Modifier le lieu" : "Nouveau lieu"}>
        <form onSubmit={save}>
          <Field label="Nom"><Input data-testid="location-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Salle omnisports, Terrain synthétique..." /></Field>
          <Field label="Adresse"><Input data-testid="location-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Rue, code postal, ville" /></Field>
          <Field label="Note (optionnel)">
            <textarea data-testid="location-note" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
              className="w-full rounded-lg bg-secondary border border-border p-3 text-sm outline-none focus:border-primary resize-y" placeholder="Accès, parking, vestiaires..." />
          </Field>
          <Button data-testid="save-location-btn" className="w-full mt-2" disabled={saving}>{saving ? "Enregistrement..." : "Enregistrer"}</Button>
        </form>
      </Modal>
    </div>
  );
}
